import { Box, Typography, Container, IconButton, Link } from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
} from '@mui/icons-material';

interface LandingPageContactProps {
  email?: string;
  phone?: string;
  website?: string;
  linkedIn?: string;
  instagram?: string;
  primaryColor: string;
}

export const LandingPageContact = ({
  email,
  phone,
  website,
  linkedIn,
  instagram,
  primaryColor,
}: LandingPageContactProps) => {
  const hasContact = email || phone || website;
  const hasSocial = linkedIn || instagram;

  if (!hasContact && !hasSocial) return null;

  // Format social URLs
  const getLinkedInUrl = (handle: string) => {
    if (handle.startsWith('http')) return handle;
    return `https://linkedin.com/in/${handle.replace('@', '')}`;
  };

  const getInstagramUrl = (handle: string) => {
    if (handle.startsWith('http')) return handle;
    return `https://instagram.com/${handle.replace('@', '')}`;
  };

  return (
    <Box
      component="section"
      id="contact"
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: 'white',
      }}
    >
      <Container maxWidth="md">
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: 700,
            mb: 4,
            textAlign: 'center',
            color: 'grey.900',
          }}
        >
          Get in Touch
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {/* Contact Info */}
          {hasContact && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 3,
                mb: 2,
              }}
            >
              {email && (
                <Link
                  href={`mailto:${email}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'grey.700',
                    textDecoration: 'none',
                    '&:hover': { color: primaryColor },
                  }}
                >
                  <EmailIcon sx={{ fontSize: 20 }} />
                  <Typography variant="body1">{email}</Typography>
                </Link>
              )}

              {phone && (
                <Link
                  href={`tel:${phone}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'grey.700',
                    textDecoration: 'none',
                    '&:hover': { color: primaryColor },
                  }}
                >
                  <PhoneIcon sx={{ fontSize: 20 }} />
                  <Typography variant="body1">{phone}</Typography>
                </Link>
              )}

              {website && (
                <Link
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'grey.700',
                    textDecoration: 'none',
                    '&:hover': { color: primaryColor },
                  }}
                >
                  <WebsiteIcon sx={{ fontSize: 20 }} />
                  <Typography variant="body1">
                    {website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </Typography>
                </Link>
              )}
            </Box>
          )}

          {/* Social Links */}
          {hasSocial && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
              }}
            >
              {linkedIn && (
                <IconButton
                  component="a"
                  href={getLinkedInUrl(linkedIn)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: 'grey.100',
                    color: 'grey.700',
                    '&:hover': {
                      bgcolor: primaryColor,
                      color: 'white',
                    },
                  }}
                >
                  <LinkedInIcon />
                </IconButton>
              )}

              {instagram && (
                <IconButton
                  component="a"
                  href={getInstagramUrl(instagram)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: 'grey.100',
                    color: 'grey.700',
                    '&:hover': {
                      bgcolor: primaryColor,
                      color: 'white',
                    },
                  }}
                >
                  <InstagramIcon />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};
